import { Card, CardContent, CardHeader } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import React, {useState, useEffect} from "react";
import { useAuth } from '../../context/authContext';
import { getUserDetails } from '../../services/user.service';
import { authorizePayment } from '../../services/payment.service';
import { useToast } from "../../components/ui/use-toast";
import { AlertCircle, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";
import CircularProgress from '@mui/material/CircularProgress';
import { useResolvedPaymentParams } from "../../hooks/useResolvedPaymentParams";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";

import {
    Alert,
    AlertDescription,
    AlertTitle,
} from "../../components/ui/alert";

import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
  } from "../../components/ui/input-otp"

export default function PaymentAuth({ paymentDetails, updatePage, setUpdatePage }) {

    const { toast } = useToast()
    const [isChecked, setIsChecked] = useState(false);
    const [otp, setOtp] = useState('');
    const { user, setUser } = useAuth();
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
    const { resolvedPaymentId, resolvedOrgId } = useResolvedPaymentParams();
    const [loading, setLoading] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectOpen, setRejectOpen] = useState(false);
    // Role in the payment's organisation, not whichever org is selected in the header.
    const [paymentOrgRole, setPaymentOrgRole] = useState(null);

    const paymentOrganisationName =
        user.organisations?.find((org) => String(org.id) === String(resolvedOrgId))?.name
        || user.organisationName;

    const approval = paymentDetails.approval;
    const isCustom = approval && approval.type === 'CUSTOM';

    // For CUSTOM payments, action is only possible against the ACTIVE step, and the
    // current user must be one of its eligible approvers. Compare ids loosely so a
    // string/number mismatch between the token user and the approver doesn't break it.
    const isCurrentUser = (id) => id !== undefined && id !== null && String(id) === String(user.id);
    const activeStep = isCustom ? (approval.steps || []).find((s) => s.status === 'ACTIVE') : null;
    const currentApprover = activeStep?.approvers?.find((a) => isCurrentUser(a.userId));
    const isEligible = isCustom ? !!currentApprover : true;

    // AUTHORISER approvals require an OTP; REVIEWER approvals do not. The role is
    // assigned per approver on the workflow step. For STANDARD payments the legacy
    // authenticator OTP flow always applies.
    const stepApprovalType = currentApprover?.approvalType;
    const needsOtp = isCustom ? stepApprovalType === 'AUTHORISER' : true;

    // A user may only approve a given step once. When a step needs multiple approvals
    // and this user has already approved it (but it's still ACTIVE awaiting others), the
    // backend marks them via the approver's hasApproved flag and the step's approvedBy
    // list. We check both. This is scoped to the active step, so if the workflow later
    // advances to another step this user approves on, that step is actionable again.
    const approvedActiveStepViaList = (activeStep?.approvedBy || []).some((a) => isCurrentUser(a.userId));
    const alreadyApprovedActiveStep =
        isCustom && (!!currentApprover?.hasApproved || approvedActiveStepViaList);
    const remainingApprovals = activeStep
        ? Math.max((activeStep.requiredApprovals ?? 0) - (activeStep.obtainedApprovals ?? 0), 0)
        : 0;

    useEffect(() => {
        const fetchUserDetails = async () => {
            const response = await getUserDetails(user.token, resolvedOrgId, user.id);
            if (response && response.status === 200) {
                const { twoFactorEnabled: twoFA, role } = response.data.data;
                setUser({ ...user, twoFactorEnabled: twoFA });
                setTwoFactorEnabled(twoFA);
                setPaymentOrgRole(role ?? null);
            }
        };

        if (resolvedOrgId) {
            fetchUserDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedOrgId]);

    const handleAuthorizePayment = async (isRejected = false, reason = undefined) => {
        setLoading(true);
        const response = await authorizePayment(
            user.token,
            resolvedPaymentId,
            needsOtp && !isRejected ? otp : '',
            resolvedOrgId,
            isRejected,
            reason,
        );
        setLoading(false);
        if (response && response.status === 200) {
            toast({
                title: isRejected ? "Rejected" : "Success",
                description: isRejected ? "Payment Rejected Successfully" : "Payment Authorised Successfully",
                variant: isRejected ? "destructive" : "success",
            });
            setOtp('');
            setRejectReason('');
            setRejectOpen(false);
            setUpdatePage((prev) => !prev);
        } else {
            const errorMessage = response?.data?.error ? response.data.error.message : isRejected ? "Payment Rejection Failed" : "Payment Authorisation Failed";
            toast({
                title: "Error",
                description: errorMessage,
                variant: "destructive",
            });
            setOtp('');
        }
    };

    const isFinalised = paymentDetails.status === 'Approved' || paymentDetails.status === 'Rejected';

    // Authenticator must be set up before an AUTHORISER (or any STANDARD approver) can act.
    const blockedOnTwoFactor = needsOtp && !twoFactorEnabled;

    // Organisation admins can reject a payment that is still in approval, at any
    // workflow step, whether or not they are an eligible approver on that step.
    // Rejection does not require an OTP, so a missing authenticator does not block it.
    const isPaymentOrgAdmin = paymentOrgRole === 'Admin'
        || (paymentOrgRole === null && String(resolvedOrgId) === String(user.organisationId) && user.role === 'Admin');
    const canAdminReject = isPaymentOrgAdmin && !isFinalised;

    const canApprove = !isFinalised
        && !blockedOnTwoFactor
        && !(isCustom && !isEligible)
        && !alreadyApprovedActiveStep;

    const rejectDialog = (
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
            <DialogTrigger asChild>
                <Button type="button" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                    Reject
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Reject Payment</DialogTitle>
                    <DialogDescription>
                        Provide a reason for rejecting this payment.
                    </DialogDescription>
                </DialogHeader>
                <Textarea
                    placeholder="Reason for rejection"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                />
                <DialogFooter>
                    <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        disabled={!rejectReason.trim()}
                        onClick={() => handleAuthorizePayment(true, rejectReason.trim())}
                    >
                        Confirm rejection
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    const adminRejectPanel = (
        <div className="mt-4 flex flex-col items-start gap-3 rounded-lg border border-slate-200 bg-white p-4">
            <p className="mb-0 text-sm text-slate-600">
                As an organisation admin, you can reject this payment at any stage.
            </p>
            {rejectDialog}
        </div>
    );

    const renderActionForm = () => (
        <form onSubmit={(e) => {
            e.preventDefault();
            handleAuthorizePayment(false);
        }}>
            {isCustom && (
                <div className="mb-5 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5">
                    <span className="text-sm text-slate-500">Your role on the active step</span>
                    <Badge variant={stepApprovalType === 'AUTHORISER' ? 'success' : 'info'}>
                        {stepApprovalType === 'AUTHORISER' ? 'Authoriser' : 'Reviewer'}
                    </Badge>
                </div>
            )}
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 cursor-pointer hover:bg-slate-50/50 transition-colors">
                <input
                    className="mt-0.5"
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => setIsChecked(!isChecked)}
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                    I authorise this payment on behalf of <span className="font-semibold text-[#2E2E2E]">{paymentOrganisationName}</span>
                </span>
            </label>
            <div className="flex flex-col items-center mt-8">
                {needsOtp ? (
                    <>
                        <p className="mb-4 text-sm text-slate-500">Enter the code from your authenticator app</p>
                        <InputOTP
                            maxLength={6}
                            value={otp}
                            onChange={(value) => setOtp(value)}
                            disabled={!isChecked}
                        >
                            <InputOTPGroup><InputOTPSlot index={0} /></InputOTPGroup>
                            <InputOTPGroup><InputOTPSlot index={1} /></InputOTPGroup>
                            <InputOTPGroup><InputOTPSlot index={2} /></InputOTPGroup>
                            <InputOTPGroup><InputOTPSlot index={3} /></InputOTPGroup>
                            <InputOTPGroup><InputOTPSlot index={4} /></InputOTPGroup>
                            <InputOTPGroup><InputOTPSlot index={5} /></InputOTPGroup>
                        </InputOTP>
                    </>
                ) : (
                    <p className="mb-4 text-sm text-slate-500 text-center max-w-sm">
                        As a reviewer, you can approve this step without an OTP code.
                    </p>
                )}
                <div className="flex flex-wrap justify-center gap-3 mt-8">
                    <Button
                        type="submit"
                        className="bg-yavrio-green hover:bg-yavrio-green-dark min-w-[160px]"
                        disabled={!isChecked || (needsOtp && otp.length < 6)}
                    >
                        {isCustom ? 'Approve step' : 'Authorise payment'}
                    </Button>
                    {rejectDialog}
                </div>
            </div>
        </form>
    );

    return (
        <div className="w-full h-full">
            <Card className="h-full flex flex-col overflow-hidden">
                <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
                    <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-yavrio-green/10 border border-yavrio-green/20">
                            <ShieldCheck className="h-4 w-4 text-yavrio-green" />
                        </span>
                        <h3 className="my-0">Authorisation</h3>
                    </div>
                </CardHeader>
                <CardContent className="flex-1 pt-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-16">
                            <CircularProgress size={32} />
                        </div>
                    ) : isFinalised ? (
                        <div className={`flex items-start gap-3 rounded-lg border p-4 ${
                            paymentDetails.status === 'Rejected'
                                ? 'border-red-200 bg-red-50'
                                : 'border-yavrio-green/20 bg-yavrio-green/5'
                        }`}>
                            {paymentDetails.status === 'Rejected' ? (
                                <XCircle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" />
                            ) : (
                                <CheckCircle2 className="h-5 w-5 shrink-0 text-yavrio-green mt-0.5" />
                            )}
                            <p className="mb-0 text-sm text-slate-700">
                                {paymentDetails.status === 'Rejected'
                                    ? 'This payment has been rejected and cannot be reauthorised.'
                                    : 'This payment has been fully approved.'}
                            </p>
                        </div>
                    ) : canApprove ? (
                        renderActionForm()
                    ) : (
                        <>
                            {blockedOnTwoFactor ? (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>Authenticator app not enabled</AlertTitle>
                                    <AlertDescription>
                                        You have not enabled your authenticator app. Go to the Settings page to connect your device.
                                    </AlertDescription>
                                </Alert>
                            ) : isCustom && !isEligible ? (
                                <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                                    <AlertCircle className="h-5 w-5 shrink-0 text-slate-400 mt-0.5" />
                                    <p className="mb-0 text-sm text-slate-600">
                                        You are not an eligible approver for the current step of this workflow. The approval
                                        timeline shows who can act next.
                                    </p>
                                </div>
                            ) : alreadyApprovedActiveStep ? (
                                <div className="flex items-start gap-3 rounded-lg border border-yavrio-green/20 bg-yavrio-green/5 p-4">
                                    <CheckCircle2 className="h-5 w-5 shrink-0 text-yavrio-green mt-0.5" />
                                    <p className="mb-0 text-sm text-slate-700">
                                        You have already approved this step. It needs {remainingApprovals} more
                                        {remainingApprovals === 1 ? ' approval' : ' approvals'} before
                                        the payment can proceed.
                                    </p>
                                </div>
                            ) : null}
                            {canAdminReject && adminRejectPanel}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
